import { Box, Sheet, Table, Typography, useTheme } from '@mui/joy'
import { useUsersApi } from 'apiProviders'
import Loading from 'components/Loading'
import { format, isSameMonth } from 'date-fns'
import { sortBy, sum, uniq } from 'ramda'
import { ListingsSummary } from './types'

export default function MonthSummary({ listings, month }: { listings: ListingsSummary; month: Date }) {
    const theme = useTheme()
    const thisMonth = isSameMonth(month, new Date())
    const usersApi = useUsersApi()

    // Get all unique agents from the listings
    const allAgentIds = uniq([
        ...listings.newListings.flatMap(listing => listing.agents?.map(a => a.userId) || []),
        ...listings.underContract.flatMap(listing => listing.agents?.map(a => a.userId) || []),
    ])

    const newListings = allAgentIds.map(agentId => ({
        agentId,
        newListings: listings.newListings.filter(
            listing =>
                listing.agents?.some(a => a.userId === agentId) &&
                listing.authorityStartDate &&
                isSameMonth(listing.authorityStartDate, month),
        ).length,
    }))

    const underContract = allAgentIds.map(agentId => {
        const filteredListings = listings.underContract.filter(listing =>
            listing.agents?.some(a => a.userId === agentId),
        )

        // Calculate contract amounts - don't split, show full contract value for each agent
        const contractAmounts = filteredListings.map(listing => {
            const contractAmount = listing.contract?.salePrice ?? 0
            return contractAmount // Show full contract value, not split
        })

        return {
            agentId,
            uc: filteredListings.length,
            avgComm:
                filteredListings.length > 0
                    ? sum(filteredListings.map(l => l.commission ?? 0)) / filteredListings.length
                    : 0,
            mtdContracts: sum(contractAmounts),
        }
    })

    const data = allAgentIds
        .map(agentId => {
            const user = usersApi.data?.find(u => u.userId === agentId)
            const agentName = user ? `${user.firstName} ${user.lastName}` : `Agent ${agentId}`

            // Get the data for this specific agent
            const agentUnderContract = underContract.find(x => x.agentId === agentId)
            const agentNewListings = newListings.find(x => x.agentId === agentId)

            return {
                agent: agentName,
                uc: agentUnderContract?.uc || 0,
                avgComm:
                    agentUnderContract && agentUnderContract.uc > 0
                        ? agentUnderContract.avgComm.toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                          }) + '%'
                        : '-',
                mtdContracts: agentUnderContract?.mtdContracts || 0,
                newListings: agentNewListings?.newListings || 0,
            }
        })
        .filter(row => row.uc > 0 || row.newListings > 0) // Only show agents with activity

    // Don't render until users data is loaded
    if (!usersApi.data) {
        return <Loading />
    }

    return (
        <Box>
            <Typography
                level="h2"
                sx={{ mb: 2 }}
            >
                {format(month, 'LLLL')}
            </Typography>
            <Sheet
                variant="outlined"
                sx={{
                    width: '100%',
                    borderRadius: 'sm',
                }}
            >
                <Table
                    sx={{
                        '& th': {
                            height: '65px',
                            backgroundColor: thisMonth ? '#8B0000' : '#002147',
                            color: 'white',
                        },
                        '& tr': {
                            '&:nth-of-type(odd)': {
                                backgroundColor: theme.palette.background.level1,
                            },
                        },
                    }}
                >
                    <thead>
                        <tr>
                            <th>Agent</th>
                            <th
                                style={{ textAlign: 'right' }}
                                align="right"
                            >
                                U/C
                            </th>
                            <th
                                style={{ textAlign: 'right' }}
                                align="right"
                            >
                                Avg Comm
                            </th>
                            <th
                                style={{ textAlign: 'right' }}
                                align="right"
                            >
                                {thisMonth && 'MTD '}Contracts $
                            </th>
                            <th
                                style={{ textAlign: 'right' }}
                                align="right"
                            >
                                New Listings
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortBy(x => -x.mtdContracts, data).map((row, index) => (
                            <tr key={index}>
                                <td>{row.agent}</td>
                                <td align="right">{row.uc}</td>
                                <td align="right">{row.avgComm}</td>
                                <td align="right">
                                    $
                                    {row.mtdContracts.toLocaleString('en-US', {
                                        maximumFractionDigits: 0,
                                    })}
                                </td>
                                <td align="right">{row.newListings}</td>
                            </tr>
                        ))}
                        <tr>
                            <td>
                                <strong>Total</strong>
                            </td>
                            <td align="right">
                                <strong>{sum(data.map(x => x.uc))}</strong>
                            </td>
                            <td align="right"></td>
                            <td align="right">
                                <strong>
                                    $
                                    {sum(data.map(x => x.mtdContracts)).toLocaleString('en-US', {
                                        maximumFractionDigits: 0,
                                    })}
                                </strong>
                            </td>
                            <td align="right">
                                <strong>{sum(data.map(x => x.newListings))}</strong>
                            </td>
                        </tr>
                    </tbody>
                </Table>
            </Sheet>
        </Box>
    )
}
